import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { setCurrentUser } from './currentUserSlice';
import type { UndecoratedInvestment } from '@flemingmetrics/api-client';
import {
  deleteCompanyInvestor,
  setCompanyInvestors,
  setInvestorCompanies,
} from '@/store/slices/companyInvestorsSlice';
import { deleteInvestor } from '@/store/slices/investorsSlice';
import { filterByValue, mergeWithoutDuplicates } from '@/utils/arrays';

type InvestmentsState = {
  byId: Record<string, UndecoratedInvestment>;
  byCompanyId: Record<string, string[]>;
  byInvestorId: Record<string, string[]>;
};

const initialState: InvestmentsState = {
  byId: {},
  byCompanyId: {},
  byInvestorId: {},
};

export const investmentsSlice = createSlice({
  name: 'investments',
  initialState,
  reducers: {
    addInvestment: (state, action: PayloadAction<UndecoratedInvestment>) => {
      return getNextState(state, [action.payload]);
    },
    setInvestments: (state, action: PayloadAction<UndecoratedInvestment[]>) => {
      return getNextState(state, action.payload);
    },
    deleteInvestment: (
      state,
      action: PayloadAction<{ investmentId: number }>
    ) => {
      const key = `${action.payload.investmentId}`;
      const investment = state.byId[key];
      if (!investment) return state;
      const { companyId, investorId } = investment;
      const byId = { ...state.byId };
      const byCompanyId = { ...state.byCompanyId };
      const byInvestorId = { ...state.byInvestorId };
      byCompanyId[companyId] = filterByValue(byCompanyId[companyId], key);
      byInvestorId[investorId] = filterByValue(byInvestorId[investorId], key);
      delete byId[key];
      return { byId, byCompanyId, byInvestorId };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCurrentUser, (state, action) => {
        return { byId: {}, byCompanyId: {}, byInvestorId: {} };
      })
      .addCase(setInvestorCompanies, (state, action) => {
        const investments = action.payload.flatMap((ic) => ic.investments);
        return getNextState(state, investments);
      })
      .addCase(setCompanyInvestors, (state, action) => {
        const investments = action.payload.flatMap((ic) => ic.investments);
        return getNextState(state, investments);
      })
      .addCase(deleteCompanyInvestor, (state, action) => {
        const { companyId, investorId, investmentIds } = action.payload;
        const byId = { ...state.byId };
        const byCompanyId = { ...state.byCompanyId };
        const byInvestorId = { ...state.byInvestorId };
        investmentIds.forEach((id) => {
          const key = `${id}`;
          if (!byId[key]) return;
          if (byCompanyId[companyId]) {
            byCompanyId[companyId] = filterByValue(byCompanyId[companyId], key);
          }
          if (byInvestorId[investorId]) {
            byInvestorId[investorId] = filterByValue(
              byInvestorId[investorId],
              key
            );
          }
          delete byId[key];
        });
        return { byId, byCompanyId, byInvestorId };
      })
      .addCase(deleteInvestor, (state, action) => {
        const { investorId } = action.payload;
        const keys = state.byInvestorId[investorId];
        if (!keys) return state;
        const byId = { ...state.byId };
        const byCompanyId = { ...state.byCompanyId };
        const byInvestorId = { ...state.byInvestorId };
        keys.forEach((key) => {
          if (!byId[key]) return;
          const { companyId } = byId[key];
          if (byCompanyId[companyId]) {
            byCompanyId[companyId] = filterByValue(byCompanyId[companyId], key);
          }
          delete byId[key];
        });
        delete byInvestorId[investorId];
        return { byId, byCompanyId, byInvestorId };
      });
  },
});

function getNextState(
  state: InvestmentsState,
  investments: UndecoratedInvestment[]
) {
  const byId = { ...state.byId };
  const byCompanyId = { ...state.byCompanyId };
  const byInvestorId = { ...state.byInvestorId };
  investments.forEach((investment) => {
    const key = `${investment.id}`;
    byId[key] = investment;
    byCompanyId[investment.companyId] = mergeWithoutDuplicates(
      byCompanyId[investment.companyId] || [],
      [key]
    );
    byInvestorId[investment.investorId] = mergeWithoutDuplicates(
      byInvestorId[investment.investorId] || [],
      [key]
    );
  });
  return { byId, byCompanyId, byInvestorId };
}

export const { addInvestment, setInvestments, deleteInvestment } =
  investmentsSlice.actions;

export default investmentsSlice.reducer;
